import getApplicationSettings from 'owa-application-settings/lib/selectors/getApplicationSettings';

/**
 * This list of safe protocols is used to prevent XSS attacks by only allowing
 * certain protocols in href and src attributes.
 *
 * This list changes so infrequently (only once in few years) that it's safe to
 * hardcode it, must match the list in
 * https://o365exchange.visualstudio.com/O365%20Core/_git/Substrate?path=/sources/dev/CTS/src/Server/TextConverters/Html/UrlSchemaChecker.cs
 */
const defaultSafeProtocols = [
    'about',
    'blob',
    'callto',
    'cid',
    'codeflow',
    'conf',
    'file',
    'ftp',
    'gopher',
    'groove',
    'http',
    'https',
    'im',
    'ipp',
    'mailto',
    'meet',
    'mhtml',
    'mms',
    'msdaipp',
    'ms-excel',
    'ms-powerpoint',
    'ms-word',
    'ms-outlook',
    'news',
    'notes',
    'onenote',
    'sip',
    'tel',
    'wais',
] as const;

let cachedAllowedUriRegExp: RegExp | undefined;

/* @internal — exposed for test isolation only */
export function resetAllowedUriRegExpCache(): void {
    cachedAllowedUriRegExp = undefined;
}

/**
 * Builds (and caches) the ALLOWED_URI_REGEXP from the default safe-protocol
 * list plus any server-configured schemas.
 */
export function getAllowedUriRegExp(): RegExp {
    if (!cachedAllowedUriRegExp) {
        const applicationSettings = getApplicationSettings('UrlValidationSettings');
        const safeProtocols = [...defaultSafeProtocols, ...applicationSettings.whiteListedSchemas];
        cachedAllowedUriRegExp = new RegExp(
            `^(?:(?:${safeProtocols.join('|')}):|[^a-z]|[a-z+.\\-]+(?:[^a-z+.\\-:]|$))`,
            'i'
        );
    }
    return cachedAllowedUriRegExp;
}

/**
 * URI-carrying attributes to scrub. Each entry carries a CSS selector
 * (namespaced colons must be escaped) and the DOM attribute name used
 * for getAttribute/removeAttribute.
 */
const URI_ATTRIBUTES: {
    selector: string;
    name: string;
}[] = [
    { selector: '[href]', name: 'href' },
    { selector: '[src]', name: 'src' },
    { selector: '[action]', name: 'action' },
    { selector: '[formaction]', name: 'formaction' },
    { selector: '[xlink\\:href]', name: 'xlink:href' },
];

/**
 * Post-sanitization URI scrubber for the native Sanitizer API path.
 *
 * The native HTML Sanitizer API has no equivalent of DOMPurify's ALLOWED_URI_REGEXP.
 * This function walks URI-carrying attributes and removes any that don't match
 * the allowed protocol list.
 *
 * @param root The sanitized DOM root to scrub URIs in.
 * @param skipUriFiltering When true (ALLOW_UNKNOWN_PROTOCOLS), skip URI filtering entirely.
 */
export function postProcessUris(
    root: Element | DocumentFragment,
    skipUriFiltering?: boolean
): void {
    if (skipUriFiltering) {
        return;
    }

    const uriRegex = getAllowedUriRegExp();

    for (const { selector, name } of URI_ATTRIBUTES) {
        const elements = root.querySelectorAll(selector);
        elements.forEach(el => {
            const value = el.getAttribute(name);
            if (value && !uriRegex.test(value)) {
                el.removeAttribute(name);
            }
        });
    }
}
